import React, { useState, useEffect } from 'react';
import { Tag, Plus, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const TaxonomyPage = () => {
  const [mainGroups, setMainGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [subgroups, setSubgroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMainGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadSubgroups(selectedGroup.code);
    }
  }, [selectedGroup]);

  const loadMainGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/taxonomy/main-groups');
      setMainGroups(response.data || []);
    } catch (error) {
      console.error('Failed to load main groups:', error);
      toast.error('Failed to load taxonomy');
    } finally {
      setLoading(false);
    }
  };

  const loadSubgroups = async (code) => {
    try {
      const response = await api.get(`/api/v1/taxonomy/subgroups/${code}`);
      setSubgroups(response.data || []);
    } catch (error) {
      console.error('Failed to load subgroups:', error);
      setSubgroups([]);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Taxonomy Management</h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage main groups and subgroups for part classification
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Groups */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Main Groups</h2>
            <span className="text-sm text-slate-500">{mainGroups.length} groups</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <div key={i} className="px-4 py-3">
                  <div className="h-4 bg-slate-200 rounded skeleton w-48"></div>
                </div>
              ))
            ) : mainGroups.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No main groups found</p>
                <p className="text-slate-400 text-sm">Run the seed script to populate taxonomy</p>
              </div>
            ) : (
              mainGroups.map((group) => (
                <button
                  key={group.code}
                  onClick={() => setSelectedGroup(group)}
                  className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center justify-between ${
                    selectedGroup?.code === group.code ? 'bg-blue-50' : ''
                  }`}
                  data-testid={`main-group-${group.code}`}
                >
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="technical-code font-semibold text-haden-primary">
                        {group.code}
                      </span>
                      <span className="text-slate-900">{group.name}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {group.part_count} parts
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Subgroups */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">
              {selectedGroup ? `Subgroups for ${selectedGroup.code}` : 'Subgroups'}
            </h2>
            {selectedGroup && (
              <span className="text-sm text-slate-500">{subgroups.length} subgroups</span>
            )}
          </div>
          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {!selectedGroup ? (
              <div className="px-4 py-12 text-center">
                <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a main group to view subgroups</p>
              </div>
            ) : subgroups.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No subgroups found for {selectedGroup.code}</p>
              </div>
            ) : (
              subgroups.map((subgroup) => (
                <div
                  key={`${subgroup.main_group_code}-${subgroup.subgroup_code}`}
                  className="px-4 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`subgroup-${subgroup.subgroup_code}`}
                >
                  <div className="flex items-center space-x-2">
                    <span className="technical-code font-semibold text-haden-secondary">
                      {subgroup.full_code}
                    </span>
                    <span className="text-slate-900">{subgroup.name}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {subgroup.part_count} parts
                  </p>
                  {subgroup.scope_notes && (
                    <p className="text-sm text-slate-400 mt-1">{subgroup.scope_notes}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxonomyPage;
